import { useState, useEffect, useCallback } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://trading-api-1075463475276.us-central1.run.app';

// Simple SVG line chart for equity curve
function EquityCurveChart({ data, initialCapital }) {
  if (!data || data.length < 2) return null;

  const width = 800;
  const height = 200;
  const padding = { top: 20, right: 20, bottom: 30, left: 60 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const equities = data.map(d => d.equity || initialCapital);
  const minEq = Math.min(...equities) * 0.998;
  const maxEq = Math.max(...equities) * 1.002;
  const range = maxEq - minEq || 1;

  const points = data.map((d, i) => {
    const x = padding.left + (i / (data.length - 1)) * chartW;
    const y = padding.top + chartH - ((d.equity - minEq) / range) * chartH;
    return `${x},${y}`;
  }).join(' ');

  const lastEq = equities[equities.length - 1];
  const isUp = lastEq >= initialCapital;

  // Fill area
  const areaPoints = `${padding.left},${padding.top + chartH} ${points} ${padding.left + chartW},${padding.top + chartH}`;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 'auto' }}>
      {/* Grid lines */}
      {[0, 0.25, 0.5, 0.75, 1].map(pct => {
        const y = padding.top + chartH * (1 - pct);
        const val = minEq + range * pct;
        return (
          <g key={pct}>
            <line x1={padding.left} y1={y} x2={padding.left + chartW} y2={y} stroke="#334155" strokeWidth="0.5" />
            <text x={padding.left - 8} y={y + 4} fill="#64748b" fontSize="10" textAnchor="end">
              ${val >= 1000 ? `${(val / 1000).toFixed(1)}k` : val.toFixed(0)}
            </text>
          </g>
        );
      })}
      {/* Initial capital line */}
      {initialCapital >= minEq && initialCapital <= maxEq && (
        <line
          x1={padding.left} y1={padding.top + chartH - ((initialCapital - minEq) / range) * chartH}
          x2={padding.left + chartW} y2={padding.top + chartH - ((initialCapital - minEq) / range) * chartH}
          stroke="#f59e0b" strokeWidth="1" strokeDasharray="4 2"
        />
      )}
      {/* Area fill */}
      <polygon points={areaPoints} fill={isUp ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)'} />
      {/* Line */}
      <polyline points={points} fill="none" stroke={isUp ? '#22c55e' : '#ef4444'} strokeWidth="2" />
      {/* Date labels */}
      {data.filter((_, i) => i === 0 || i === data.length - 1 || i === Math.floor(data.length / 2)).map((d, i) => {
        const idx = i === 0 ? 0 : i === 1 ? Math.floor(data.length / 2) : data.length - 1;
        const x = padding.left + (idx / (data.length - 1)) * chartW;
        return (
          <text key={i} x={x} y={height - 5} fill="#64748b" fontSize="10" textAnchor="middle">
            {data[idx]?.date?.slice(0, 10) || ''}
          </text>
        );
      })}
    </svg>
  );
}

export default function PaperTrades() {
  const [paperTrades, setPaperTrades] = useState({ trades: [], summary: [] });
  const [portfolios, setPortfolios] = useState([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [equityCurve, setEquityCurve] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPaperTrades = useCallback(async () => {
    try {
      const [tradesRes, summaryRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/paper-trades?limit=50`),
        fetch(`${API_BASE_URL}/api/paper-trades/summary`)
      ]);
      const tradesData = await tradesRes.json();
      const summaryData = await summaryRes.json();

      setPaperTrades({
        trades: tradesData.success ? tradesData.trades : [],
        summary: summaryData.success ? summaryData.summaries : []
      });
    } catch (error) {
      console.error('Error fetching paper trades:', error);
    }
  }, []);

  const fetchPortfolios = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/paper/portfolios`);
      const data = await res.json();
      if (data.portfolios) {
        setPortfolios(data.portfolios);
        if (data.portfolios.length > 0 && !selectedPortfolio) {
          setSelectedPortfolio(data.portfolios[0].portfolio_id);
        }
      }
    } catch (error) {
      console.error('Error fetching portfolios:', error);
    }
  }, [selectedPortfolio]);

  const fetchEquityCurve = useCallback(async (portfolioId) => {
    if (!portfolioId) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/paper/equity-curve/${portfolioId}`);
      const data = await res.json();
      setEquityCurve(data);
    } catch (error) {
      console.error('Error fetching equity curve:', error);
    }
  }, []);

  const fetchPerformance = useCallback(async (portfolioId) => {
    if (!portfolioId) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/paper/performance/${portfolioId}`);
      const data = await res.json();
      if (!data.error) setPerformance(data);
    } catch (error) {
      console.error('Error fetching performance:', error);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchPaperTrades(), fetchPortfolios()]);
      setLoading(false);
    };
    load();
  }, [fetchPaperTrades, fetchPortfolios]);

  useEffect(() => {
    if (selectedPortfolio) {
      fetchEquityCurve(selectedPortfolio);
      fetchPerformance(selectedPortfolio);
    }
  }, [selectedPortfolio, fetchEquityCurve, fetchPerformance]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px', color: '#64748b' }}>
        Loading paper trades...
      </div>
    );
  }

  // Calculate summary stats from trades
  const totalTrades = paperTrades.trades.length;
  const openTrades = paperTrades.trades.filter(t => t.status === 'open').length;
  const closedTrades = paperTrades.trades.filter(t => t.status === 'closed');
  const totalPnl = closedTrades.reduce((sum, t) => sum + (t.net_pnl || 0), 0);
  const winners = closedTrades.filter(t => (t.net_pnl || 0) > 0).length;
  const winRate = closedTrades.length > 0 ? (winners / closedTrades.length * 100) : 0;

  const perfMetrics = performance?.metrics;
  const pnlStats = performance?.pnl_stats;

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Portfolio Selector */}
      {portfolios.length > 0 && (
        <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ color: '#94a3b8', fontSize: '14px' }}>Portfolio:</span>
          <select
            value={selectedPortfolio || ''}
            onChange={(e) => setSelectedPortfolio(e.target.value)}
            style={{
              background: '#0f172a', border: '1px solid #334155', borderRadius: '6px',
              padding: '8px 12px', color: '#f1f5f9', fontSize: '14px'
            }}
          >
            {portfolios.map(p => (
              <option key={p.portfolio_id} value={p.portfolio_id}>
                {p.name || p.portfolio_id} — ${(p.current_equity || p.initial_capital || 100000).toLocaleString()}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        <div style={{ background: '#0f172a', borderRadius: '8px', padding: '16px', border: '1px solid #334155' }}>
          <div style={{ color: '#64748b', fontSize: '12px' }}>Total Trades</div>
          <div style={{ color: '#f1f5f9', fontSize: '28px', fontWeight: 'bold' }}>{totalTrades}</div>
        </div>
        <div style={{ background: '#0f172a', borderRadius: '8px', padding: '16px', border: '1px solid #334155' }}>
          <div style={{ color: '#64748b', fontSize: '12px' }}>Open</div>
          <div style={{ color: '#f59e0b', fontSize: '28px', fontWeight: 'bold' }}>{openTrades}</div>
        </div>
        <div style={{ background: '#0f172a', borderRadius: '8px', padding: '16px', border: '1px solid #334155' }}>
          <div style={{ color: '#64748b', fontSize: '12px' }}>Win Rate</div>
          <div style={{ color: winRate >= 50 ? '#22c55e' : '#ef4444', fontSize: '28px', fontWeight: 'bold' }}>{winRate.toFixed(1)}%</div>
        </div>
        <div style={{ background: '#0f172a', borderRadius: '8px', padding: '16px', border: '1px solid #334155' }}>
          <div style={{ color: '#64748b', fontSize: '12px' }}>Total P&L</div>
          <div style={{ color: totalPnl >= 0 ? '#22c55e' : '#ef4444', fontSize: '28px', fontWeight: 'bold' }}>
            {totalPnl >= 0 ? '+' : ''}${totalPnl.toFixed(2)}
          </div>
        </div>
        {perfMetrics?.profit_factor != null && (
          <div style={{ background: '#0f172a', borderRadius: '8px', padding: '16px', border: '1px solid #334155' }}>
            <div style={{ color: '#64748b', fontSize: '12px' }}>Profit Factor</div>
            <div style={{ color: perfMetrics.profit_factor >= 1.5 ? '#22c55e' : perfMetrics.profit_factor >= 1 ? '#f59e0b' : '#ef4444', fontSize: '28px', fontWeight: 'bold' }}>
              {perfMetrics.profit_factor.toFixed(2)}
            </div>
          </div>
        )}
        {pnlStats?.largest_win != null && (
          <div style={{ background: '#0f172a', borderRadius: '8px', padding: '16px', border: '1px solid #334155' }}>
            <div style={{ color: '#64748b', fontSize: '12px' }}>Best / Worst</div>
            <div style={{ fontSize: '14px' }}>
              <span style={{ color: '#22c55e', fontWeight: '600' }}>+${(pnlStats.largest_win || 0).toFixed(0)}</span>
              <span style={{ color: '#64748b' }}> / </span>
              <span style={{ color: '#ef4444', fontWeight: '600' }}>${(pnlStats.largest_loss || 0).toFixed(0)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Equity Curve */}
      {equityCurve?.equity_curve?.length > 1 && (
        <div style={{ background: '#1e293b', borderRadius: '12px', padding: '20px', marginBottom: '24px', border: '1px solid #334155' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ color: '#f1f5f9', margin: 0, fontSize: '16px' }}>Equity Curve</h3>
            <span style={{ color: '#64748b', fontSize: '12px' }}>
              {equityCurve.data_points} data points | Initial: ${(equityCurve.initial_capital || 100000).toLocaleString()}
            </span>
          </div>
          <EquityCurveChart data={equityCurve.equity_curve} initialCapital={equityCurve.initial_capital || 100000} />
        </div>
      )}

      {/* Strategy Summaries */}
      {paperTrades.summary.length > 0 && (
        <div style={{ background: '#1e293b', borderRadius: '12px', padding: '20px', marginBottom: '24px', border: '1px solid #334155' }}>
          <h3 style={{ color: '#f1f5f9', margin: '0 0 16px 0', fontSize: '16px' }}>Performance by Strategy</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
            {paperTrades.summary.map((s, i) => (
              <div key={i} style={{ background: '#0f172a', borderRadius: '8px', padding: '14px', border: '1px solid #334155' }}>
                <div style={{ color: '#f1f5f9', fontWeight: '500', marginBottom: '8px' }}>{s.strategy || 'Unknown'}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                  <span style={{ color: '#94a3b8' }}>Trades: {s.total_trades || 0}</span>
                  <span style={{ color: (s.total_pnl || 0) >= 0 ? '#22c55e' : '#ef4444', fontWeight: '600' }}>
                    ${(s.total_pnl || 0).toFixed(2)}
                  </span>
                </div>
                {s.win_rate != null && (
                  <div style={{ color: '#64748b', fontSize: '12px', marginTop: '4px' }}>
                    Win Rate: {(s.win_rate * 100).toFixed(1)}%
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trades Table */}
      <div style={{ background: '#1e293b', borderRadius: '12px', padding: '20px', border: '1px solid #334155' }}>
        <h3 style={{ color: '#f1f5f9', margin: '0 0 16px 0', fontSize: '16px' }}>Trade History</h3>
        {paperTrades.trades.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#0f172a' }}>
                  <th style={{ color: '#64748b', padding: '12px', textAlign: 'left', fontSize: '12px' }}>Symbol</th>
                  <th style={{ color: '#64748b', padding: '12px', textAlign: 'left', fontSize: '12px' }}>Strategy</th>
                  <th style={{ color: '#64748b', padding: '12px', textAlign: 'left', fontSize: '12px' }}>Type</th>
                  <th style={{ color: '#64748b', padding: '12px', textAlign: 'right', fontSize: '12px' }}>Entry</th>
                  <th style={{ color: '#64748b', padding: '12px', textAlign: 'right', fontSize: '12px' }}>Exit</th>
                  <th style={{ color: '#64748b', padding: '12px', textAlign: 'right', fontSize: '12px' }}>P&L</th>
                  <th style={{ color: '#64748b', padding: '12px', textAlign: 'left', fontSize: '12px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {paperTrades.trades.map(trade => (
                  <tr key={trade.trade_id} style={{ borderBottom: '1px solid #334155' }}>
                    <td style={{ color: '#f1f5f9', padding: '12px', fontWeight: '500' }}>{trade.symbol}</td>
                    <td style={{ color: '#94a3b8', padding: '12px', fontSize: '13px' }}>{trade.strategy || '-'}</td>
                    <td style={{ color: trade.trade_type === 'BUY' ? '#22c55e' : '#ef4444', padding: '12px', fontWeight: '500' }}>{trade.trade_type}</td>
                    <td style={{ color: '#94a3b8', padding: '12px', textAlign: 'right' }}>${trade.entry_price?.toFixed(2)}</td>
                    <td style={{ color: '#94a3b8', padding: '12px', textAlign: 'right' }}>{trade.exit_price ? `$${trade.exit_price.toFixed(2)}` : '-'}</td>
                    <td style={{ color: (trade.net_pnl || 0) >= 0 ? '#22c55e' : '#ef4444', padding: '12px', textAlign: 'right', fontWeight: '600' }}>
                      {trade.net_pnl != null ? `${trade.net_pnl >= 0 ? '+' : ''}$${trade.net_pnl.toFixed(2)}` : '-'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        background: trade.status === 'open' ? '#f59e0b' : trade.status === 'closed' ? '#22c55e' : '#64748b',
                        color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px'
                      }}>
                        {trade.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ color: '#64748b', textAlign: 'center', padding: '40px' }}>
            No paper trades yet. Paper trades will appear here when strategies generate signals.
          </div>
        )}
      </div>
    </div>
  );
}
